import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, ShoppingCart, Plus, Minus, Truck, Shield, RotateCcw, Check, Monitor, MonitorSmartphone, Lock, Settings, LogIn, Award, Infinity, DollarSign, Globe, Heart, Zap, RefreshCw, Clock, Sparkles, Crown, Users } from 'lucide-react';
import { getAppProducts, getApp1Products, getApp3Products, getApp6Products, getUnsoldCards } from '../api/productApi';
import { getProductImages } from '../config/productImages';
import { getProductDetail } from '../config/productDetailConfig';
import { getProductDetailI18n, getProductFAQ } from '../config/productDetailConfigI18n';
import { productPlatformMapping } from '../config/productMapping';
import { getHomeProductImage, getProductTypeByProductId } from '../config/homeProductsConfig';
import ProductComparison from './ProductComparison';
import OfficialProductComparison from './OfficialProductComparison';
import ProductDetailHero from './ProductDetailHero';
import ProductFeaturesBenefits from './ProductFeaturesBenefits';
import RiskFreeGuarantees from './RiskFreeGuarantees';
import UniversalFAQ from './UniversalFAQ';
import { productMapping, getProductIdMappingReverse } from '../config/productMapping';
import { SKU_DIMENSIONS, SKU_CONFIG, getSkusByProductType, getUserConfigsByProductType, getDurationsByProductType, getSkuByDimensions } from '../config/productSkuConfig';
import { getProductTypeContent } from '../config/productTypeContent';
import { getProductTypeContentI18n } from '../config/productTypeContentI18n';

const ProductDetailPage = () => {
 const { t, i18n } = useTranslation();
 
 // 从URL获取参数
 const { productId } = useParams();
 const [searchParams] = useSearchParams();
 const type = searchParams.get('type');
 const navigate = useNavigate();
 
 // 获取登录状态和用户信息
 const { isLoggedIn, user } = useSelector((state) => state.user);
 
 const [selectedImage, setSelectedImage] = useState(0);
 const [selectedTab, setSelectedTab] = useState('');
 const [quantity, setQuantity] = useState(1);
 const [product, setProduct] = useState(null);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState('');
 const [email, setEmail] = useState('');
 const [realStockStatus, setRealStockStatus] = useState(null); // null: 未检查, true: 有货, false: 没货
 const [stockLoading, setStockLoading] = useState(false);
 const [emailShaking, setEmailShaking] = useState(false); // 控制邮箱输入框抖动
 const [showRenewalModal, setShowRenewalModal] = useState(false); // 控制续费确认弹窗
 
 // 新增SKU相关状态
 const [selectedProductType, setSelectedProductType] = useState(null); // 选中的产品类型
 const [selectedUserConfig, setSelectedUserConfig] = useState(null); // 选中的用户配置
 const [selectedDuration, setSelectedDuration] = useState(null); // 选中的有效期
 const [availableUserConfigs, setAvailableUserConfigs] = useState([]); // 可用的用户配置
 const [availableDurations, setAvailableDurations] = useState([]); // 可用的有效期选项
 const [currentSku, setCurrentSku] = useState(null); // 当前选中的SKU
 
 // 折叠展开状态
 const [isProductTypeExpanded, setIsProductTypeExpanded] = useState(false); // 产品类型是否展开
 const [isUserConfigExpanded, setIsUserConfigExpanded] = useState(false); // 用户配置是否展开
 
 
 // 添加按钮引用和邮箱输入框引用
 const buyButtonRef = useRef(null);
 const emailInputRef = useRef(null);

 // 根据登录状态设置邮箱
 useEffect(() => {
 if (isLoggedIn && user) {
 // 优先使用uid，如果没有则使用email
 const userEmail = user.uid || user.email;
 if (userEmail) {
 setEmail(userEmail);
 } else {
 setEmail(''); // 如果都没有，留空
 }
 } else {
 setEmail(''); // 如果没登录，留空
 }
 }, [isLoggedIn, user]);

 // 从URL参数初始化SKU选择状态 - 只使用productId，不依赖type参数
 useEffect(() => {
 if (productId) {
 console.log('从URL获取的productId:', productId);
 // 根据productId在SKU配置中查找对应的SKU
 const matchingSku = Object.values(SKU_CONFIG).find(sku => sku.productId.toString() === productId);
 if (matchingSku) {
 console.log('根据productId找到匹配的SKU:', matchingSku);
 setSelectedProductType(matchingSku.productType);
 setSelectedUserConfig(matchingSku.userConfig);
 setSelectedDuration(matchingSku.duration);
 } else {
 console.log('未找到productId对应的SKU:', productId);
 console.log('可用的SKU配置:', Object.values(SKU_CONFIG).map(sku => ({ productId: sku.productId, id: sku.id })));
 // 设置默认值
 setSelectedProductType('official');
 setSelectedUserConfig('1user');
 setSelectedDuration('1month');
 }
 }
 }, [productId]);

 // 添加抖动效果
 useEffect(() => {
 if (realStockStatus !== false && !stockLoading && email && email.includes('@') && buyButtonRef.current) {
 const interval = setInterval(() => {
 if (buyButtonRef.current) {
 buyButtonRef.current.animate([
 { transform: 'translateX(0)' },
 { transform: 'translateX(-2px)' },
 { transform: 'translateX(2px)' },
 { transform: 'translateX(-2px)' },
 { transform: 'translateX(2px)' },
 { transform: 'translateX(0)' }
 ], {
 duration: 400,
 iterations: 1
 });
 }
 }, 3000); // 每3秒抖动一次

 return () => clearInterval(interval);
 }
 }, [realStockStatus, stockLoading, email]);

 // 当产品类型改变时，更新可用的用户配置
 useEffect(() => {
 const userConfigs = getUserConfigsByProductType(selectedProductType);
 setAvailableUserConfigs(userConfigs);
 
 // 如果当前选中的用户配置不在新的可用配置中，选择第一个
 if (userConfigs.length > 0 && !userConfigs.includes(selectedUserConfig)) {
 setSelectedUserConfig(userConfigs[0]);
 }
 }, [selectedProductType]);

 // 当产品类型或用户配置改变时，更新可用的有效期选项
 useEffect(() => {
 const durations = getDurationsByProductType(selectedProductType, selectedUserConfig);
 setAvailableDurations(durations);
 
 // 如果当前选中的有效期不在新的可用选项中，选择第一个
 if (durations.length > 0 && !durations.find(d => d.duration === selectedDuration)) {
 setSelectedDuration(durations[0].duration);
 }
 }, [selectedProductType, selectedUserConfig]);

 // 当SKU相关参数改变时，更新当前SKU和URL
 useEffect(() => {
 let sku = null;
 // 只有当所有必要参数都存在时才生成SKU
 if (selectedProductType && selectedUserConfig && selectedDuration) {
 sku = getSkuByDimensions(selectedProductType, selectedUserConfig, selectedDuration);
 setCurrentSku(sku);
 console.log('当前选中的SKU:', sku);
 } else {
 setCurrentSku(null);
 console.log('SKU参数不完整，清空currentSku');
 }
 
 // 同步更新URL参数，但只有当SKU真正变化时才更新
 if (sku && sku.productId) {
 const newSearchParams = new URLSearchParams(searchParams);
 newSearchParams.set('type', type || 'gpt'); // 保持原type参数
 
 // 更新URL路径中的productId
 const newPath = `/product/${sku.productId}`;
 const newUrl = `${newPath}?${newSearchParams.toString()}`;
 
 // 只有当URL真的需要改变时才进行导航
 if (window.location.pathname !== newPath || window.location.search !== `?${newSearchParams.toString()}`) {
 navigate(newUrl, { replace: true }); // 使用replace避免历史记录堆积
 }
 }
 }, [selectedProductType, selectedUserConfig, selectedDuration, navigate, searchParams, type]);

 // 从配置文件获取产品数据
 useEffect(() => {
 console.log('=== ProductDetailPage 组件加载 ===');
 console.log('当前URL:', window.location.href);
 console.log('从useParams获取的productId:', productId);
 console.log('从useSearchParams获取的type:', type);
 console.log('参数获取状态:', {
 productId: productId || 'undefined',
 type: type || 'null',
 searchParams: Object.fromEntries(searchParams)
 });

 const fetchProduct = async () => {
 if (!type) {
 console.error('产品类型为空:', type);
 setError('产品类型不存在');
 setLoading(false);
 return;
 }

 console.log('=== 开始从配置文件获取产品数据 ===');
 setLoading(true);
 setError('');
 
 try {
 console.log('获取产品详情:', {
 productId,
 type,
 '调用函数': 'getProductDetail',
 '传递参数': { type, productId }
 });

 // 从配置文件获取产品详情
 // 使用国际化版本的配置（如果存在）
 const productData = getProductDetailI18n(type) || getProductDetail(type, productId);
 
 console.log('=== 配置文件返回的产品数据 ===');
 console.log('产品数据:', productData);
 
 if (productData) {
 setProduct(productData);
 } else {
 console.log('未找到产品配置');
 setError('产品未找到');
 }
 
 } catch (err) {
 console.error('获取产品详情失败:', {
 error: err,
 message: err.message,
 productId,
 type
 });
 setError(`获取产品详情失败: ${err.message}`);
 } finally {
 setLoading(false);
 }
 };

 fetchProduct();
 }, [productId, type, i18n.language]);

 // 安全获取产品数据的辅助函数
 const getProductData = () => {
 if (!product) return null;
 
 console.log('=== 开始数据适配 ===');
 console.log('原始product数据:', product);
 
 // 处理versions数组，转换为tabs对象
 let tabs = {};
 if (product.versions && Array.isArray(product.versions)) {
 console.log('发现versions数组:', product.versions);
 product.versions.forEach(version => {
 console.log('处理版本:', version);
 if (version.label) {
 tabs[version.label] = {
 price: version.price || 0,
 originalPrice: version.originalPrice || version.price || 0,
 stock: version.stock !== undefined ? version.stock : 99,
 product_id: version.productId,
 key: version.key,
 duration: version.duration,
 features: version.features
 };
 }
 });
 console.log('转换后的tabs对象:', tabs);
 }

 // 处理图片 - 根据选中的产品类型动态获取
 let images = [];
 const urlProductId = productId; // 从URL获取的产品ID
 console.log('=== 产品详情页图片选择调试 ===');
 console.log('URL中的产品ID:', urlProductId);
 console.log('产品数据中的id:', product.id);
 console.log('当前选中的产品类型:', selectedProductType);
 
 // 根据选中的产品类型获取对应的图片
 if (selectedProductType === 'official-shared') {
 // 共享账户使用特定的图片逻辑
 const userConfig = selectedUserConfig || '3users'; // 默认3人
 console.log('共享账户类型，用户配置:', userConfig);
 
 if (userConfig === '3users') {
 images = ['/images/gpt/3.jpg'];
 console.log('✅ 使用3人共享图片');
 } else if (userConfig === '6users') {
 images = ['/images/gpt/6.jpg'];
 console.log('✅ 使用6人共享图片');
 } else {
 images = ['/images/gpt-main.jpg'];
 console.log('✅ 使用默认共享图片');
 }
 } else {
 // 其他产品类型：首先尝试从首页配置获取主图
 const homeProductImage = getHomeProductImage(urlProductId);
 if (homeProductImage) {
 console.log('从首页配置获取的主图:', homeProductImage);
 images = [homeProductImage];
 } else {
 // 根据产品类型获取图片 - 简单直接的逻辑
 const typeForImages = selectedProductType || type || 'default';
 console.log('使用产品类型获取图片:', typeForImages);
 
 // 直接映射产品类型到图片
 switch(typeForImages) {
 case 'renew':
 case 'official-renewal':
 images = ['/images/renew-main.jpg'];
 console.log('✅ 续费产品使用续费主图');
 break;
 case 'official':
 images = ['/images/gpt-main.jpg'];
 console.log('✅ 官网独享使用GPT主图');
 break;
 case 'gpt':
 default:
 const allImages = getProductImages(typeForImages);
 images = allImages && allImages.length > 0 ? [allImages[0]] : ['/images/gpt-main.jpg'];
 console.log('✅ 使用默认图片逻辑');
 break;
 }
 }
 }
 console.log('最终的图片数组:', images);

 // 获取动态文案内容
 // 对于ChatGPT相关产品，统一使用'gpt'作为产品ID
 let productIdForContent = product.id || type;
 if (type === 'gpt' || product.name?.includes('ChatGPT')) {
 productIdForContent = 'gpt';
 }
 // 使用国际化版本的产品类型内容
 const typeContent = getProductTypeContentI18n(productIdForContent, selectedProductType, t);
 
 const adaptedData = {
 id: product.id || productId,
 name: product.name || type || 'AI Product',
 images: images,
 tabs: tabs,
 description: typeContent.description || product.description || t('productDetailPage.defaultProduct.description'),
 highlights: product.highlights || [t('productDetailPage.defaultProduct.description')],
 features: product.features || [],
 specs: product.specs || [],
 aboutProduct: typeContent.aboutProduct || product.aboutProduct || [],
 afterPurchase: typeContent.afterPurchase || product.afterPurchase || [],
 importantNotes: typeContent.importantNotes || product.importantNotes || [],
 // 保存原始数据中的有用信息
 type: product.type || type || 'web'
 };

 console.log('=== 最终适配的数据 ===');
 console.log('适配后的完整数据:', adaptedData);
 
 return adaptedData;
 };

 // 使用useMemo确保selectedProductType改变时重新计算productData
 const productData = React.useMemo(() => getProductData(), [product, selectedProductType]);

 // 当SKU选择改变时，检查库存状态
 useEffect(() => {
 if (currentSku && currentSku.type) {
 console.log('当前SKU已选择，开始检查库存');
 console.log('当前SKU:', currentSku);
 // 直接使用currentSku中的productId，而不是productData.id
 checkRealStock(currentSku.productId, currentSku.type, currentSku);
 }
 }, [currentSku]);

 // 如果有产品数据但没有选中的tab，默认选择第一个
 useEffect(() => {
 if (productData && productData.tabs && !selectedTab) {
 // 找出总价最贵的套餐
 let maxPriceTab = null;
 let maxPrice = 0;
 
 Object.entries(productData.tabs).forEach(([tabKey, tabData]) => {
 if (tabData.price > maxPrice) {
 maxPrice = tabData.price;
 maxPriceTab = tabKey;
 }
 });
 
 // 如果找到了最贵的套餐，就选择它；否则选择第一个
 if (maxPriceTab) {
 setSelectedTab(maxPriceTab);
 } else {
 setSelectedTab(Object.keys(productData.tabs)[0]);
 }
 }
 }, [productData, selectedTab]);

 // 已移除：const currentProduct = productData && selectedTab ? productData.tabs[selectedTab] : null;

 // 图片相关函数
 const nextImage = () => {
 if (productData && productData.images && productData.images.length > 1) {
 setSelectedImage((prev) => (prev + 1) % productData.images.length);
 }
 };

 const prevImage = () => {
 if (productData && productData.images && productData.images.length > 1) {
 setSelectedImage((prev) => (prev - 1 + productData.images.length) % productData.images.length);
 }
 };

 // 数量控制函数（基于SKU）
 const increaseQuantity = () => {
 // SKU配置中没有库存限制，保持为1或根据业务需求设定最大值
 if (quantity < 10) { // 假设最大购买10个
 setQuantity(prev => prev + 1);
 }
 };

 const decreaseQuantity = () => {
 if (quantity > 1) {
 setQuantity(prev => prev - 1);
 }
 };

 // 处理购买流程
 const proceedToPurchase = () => {
 console.log('购买产品:', {
 selectedProductType,
 selectedUserConfig,
 selectedDuration,
 currentSku,
 quantity: 1,
 email,
 总价: currentSku.price
 });

 // 跳转到订单确认页面，传递SKU信息
 const searchParams = new URLSearchParams();
 searchParams.set('email', email);
 searchParams.set('productId', currentSku.productId);
 searchParams.set('skuId', currentSku.id);
 searchParams.set('quantity', '1');
 searchParams.set('productType', selectedProductType);
 searchParams.set('userConfig', selectedUserConfig);
 searchParams.set('duration', selectedDuration);
 searchParams.set('productName', t(currentSku.nameKey));
 searchParams.set('price', currentSku.price);
 searchParams.set('originalPrice', currentSku.originalPrice);
 searchParams.set('currency', currentSku.currency);
 searchParams.set('platform', currentSku.features.platform);
 searchParams.set('users', currentSku.features.users);
 searchParams.set('warranty', currentSku.features.warranty);
 
 navigate(`/detail/info?${searchParams.toString()}`);
 };

 // 邮箱输入框抖动函数
 const triggerEmailShake = () => {
 setEmailShaking(true);
 if (emailInputRef.current) {
 emailInputRef.current.focus();
 emailInputRef.current.animate([
 { transform: 'translateX(0)' },
 { transform: 'translateX(-8px)' },
 { transform: 'translateX(8px)' },
 { transform: 'translateX(-8px)' },
 { transform: 'translateX(8px)' },
 { transform: 'translateX(-4px)' },
 { transform: 'translateX(4px)' },
 { transform: 'translateX(0)' }
 ], {
 duration: 600,
 iterations: 1
 });
 }
 // 3秒后停止抖动状态
 setTimeout(() => {
 setEmailShaking(false);
 }, 3000);
 };

 // 检查真实库存状态
 const checkRealStock = async (productId, skuType, currentSkuInfo = null) => {
 console.log('=== 开始检查库存状态 ===');
 console.log('产品ID:', productId);
 console.log('SKU类型(type):', skuType);
 console.log('当前产品类型:', selectedProductType);
 console.log('当前SKU信息:', currentSkuInfo);
 
 // 优先使用currentSkuInfo的productType，如果没有则使用selectedProductType
 const productType = currentSkuInfo?.productType || selectedProductType;
 console.log('最终使用的productType:', productType);
 
 // 续费产品只检查有效期额度
 if (productType === 'official-renewal') {
 console.log('续费产品，检查有效期额度');
 console.log('即将执行续费产品库存检查逻辑');
 setStockLoading(true);
 
 try {
 const response = await getUnsoldCards();
 const availableMonths = response.data || 0;
 console.log('可用有效期额度（月）:', availableMonths);
 
 // 获取当前产品需要的月份数 - 直接使用传入的currentSkuInfo
 console.log('查找SKU - productId:', productId, 'currentSkuInfo:', currentSkuInfo);
 const matchingSku = currentSkuInfo || Object.values(SKU_CONFIG).find(sku => sku.productId.toString() === productId);
 console.log('找到的SKU:', matchingSku);
 // 修复月份计算逻辑：使用精确的月份映射，避免365/30=13的问题
 let neededMonths = 0;
 if (matchingSku) {
 // 根据duration字段精确获取月份数
 switch(matchingSku.duration) {
 case '1month':
 neededMonths = 1;
 break;
 case '3months':
 neededMonths = 3;
 break;
 case '12months':
 neededMonths = 12;
 break;
 default:
 // 兜底逻辑：如果没有匹配的duration，使用原有计算方式
 neededMonths = Math.ceil(matchingSku.product_days / 30);
 }
 }
 console.log('当前产品需要月份数:', neededMonths);
 
 if (availableMonths >= neededMonths) {
 console.log('有效期额度充足，设置为有货');
 setRealStockStatus(true);
 } else {
 console.log('有效期额度不足，设置为没货');
 setRealStockStatus(false);
 }
 } catch (error) {
 console.error('检查有效期额度失败:', error);
 setRealStockStatus(false); // 接口失败时默认设置为没货
 } finally {
 setStockLoading(false);
 }
 console.log('续费产品库存检查完成，即将return');
 return;
 }
 
 // 根据SKU类型调用不同的库存接口
 const needCheckStock = ['app', 'app1', 'app3', 'app6', 'renew'].includes(skuType?.toLowerCase());
 console.log('进入普通库存检查逻辑，needCheckStock:', needCheckStock);
 
 if (needCheckStock) {
 console.log(`检测到需要检查库存的SKU类型: ${skuType}，调用库存接口`);
 setStockLoading(true);
 
 try {
 let response;
 
 // 根据不同的SKU类型调用对应的接口
 switch(skuType?.toLowerCase()) {
 case 'app':
 response = await getAppProducts();
 break;
 case 'app1':
 case 'renew': // 官网续费产品也需要检查账号库存和有效期额度
 // app1 需要同时检查账号库存和有效期额度
 const [accountResponse, cardResponse] = await Promise.all([
 getApp1Products().catch(error => {
 console.error('获取app1账号库存失败:', error);
 return { data: 0 };
 }),
 getUnsoldCards().catch(error => {
 console.error('获取有效期额度失败:', error);
 return { data: 0 };
 })
 ]);
 
 // 处理账号库存
 let accountStock = 0;
 if (typeof accountResponse.data === 'string') {
 accountStock = parseInt(accountResponse.data, 10);
 } else if (typeof accountResponse.data === 'number') {
 accountStock = accountResponse.data;
 }
 
 // 处理有效期额度
 const availableMonths = cardResponse.data || 0;
 
 // 获取当前产品需要的月份数 - 直接使用传入的currentSkuInfo
 const matchingSku = currentSkuInfo || Object.values(SKU_CONFIG).find(sku => sku.productId.toString() === productId);
 // 修复月份计算逻辑：使用精确的月份映射，避免365/30=13的问题
 let neededMonths = 0;
 if (matchingSku) {
 // 根据duration字段精确获取月份数
 switch(matchingSku.duration) {
 case '1month':
 neededMonths = 1;
 break;
 case '3months':
 neededMonths = 3;
 break;
 case '12months':
 neededMonths = 12;
 break;
 default:
 // 兜底逻辑：如果没有匹配的duration，使用原有计算方式
 neededMonths = Math.ceil(matchingSku.product_days / 30);
 }
 }
 
 console.log('app1 库存检查结果:');
 console.log('- 账号库存:', accountStock);
 console.log('- 可用有效期额度（月）:', availableMonths);
 console.log('- 需要月份数:', neededMonths);
 
 // 两个条件都满足才有货
 if ((isNaN(accountStock) || accountStock < 1) || (availableMonths < neededMonths)) {
 console.log('app1 库存不足（账号或有效期不足），设置为没货');
 setRealStockStatus(false);
 } else {
 console.log('app1 库存充足（账号和有效期都满足），设置为有货');
 setRealStockStatus(true);
 }
 
 setStockLoading(false);
 return; // 直接返回，不再执行后续代码
 case 'app3':
 response = await getApp3Products();
 break;
 case 'app6':
 response = await getApp6Products();
 break;
 default:
 console.log('未知的SKU类型');
 setRealStockStatus(true);
 return;
 }
 
 console.log('库存接口返回:', response);
 
 // 处理返回的数据，支持两种格式：number 或 string
 let stockCount = 0;
 if (response && response.data !== undefined) {
 // 如果 data 是字符串，尝试转换为数字
 if (typeof response.data === 'string') {
 stockCount = parseInt(response.data, 10);
 } else if (typeof response.data === 'number') {
 stockCount = response.data;
 }
 
 console.log('库存数量:', stockCount);
 
 if (isNaN(stockCount) || stockCount < 1) {
 console.log('库存不足，设置为没货');
 setRealStockStatus(false);
 } else {
 console.log('库存充足，设置为有货');
 setRealStockStatus(true);
 }
 } else {
 console.log('接口返回数据格式异常，默认设置为没货');
 setRealStockStatus(false);
 }
 } catch (error) {
 console.error('库存检查失败:', error);
 setRealStockStatus(false); // 接口失败时默认设置为没货
 } finally {
 setStockLoading(false);
 }
 } else {
 console.log('非需要检查库存的SKU类型，直接设置为有货');
 setRealStockStatus(true); // 其他产品直接设置为有货
 }
 };

 if (loading) {
 return (
 <div className="min-h-screen bg-cream-50 flex items-center justify-center">
 <div className="text-center">
 <div className="w-16 h-16 border-4 border-accent-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
 <p className="text-ink-600 text-lg">{t('productDetailPage.loading')}</p>
 <p className="text-ink-500 text-sm mt-2">{t('productDetailPage.productId')}: {productId}</p>
 <p className="text-ink-500 text-sm">{t('productDetailPage.productType')}: {type}</p>
 </div>
 </div>
 );
 }

 if (error) {
 return (
 <div className="min-h-screen bg-cream-50 flex items-center justify-center">
 <div className="text-center max-w-md mx-auto px-6">
 <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
 <ShoppingCart className="w-12 h-12 text-red-400" />
 </div>
 <h1 className="text-3xl font-bold text-ink-900 mb-4">{t('productDetailPage.loadFailed')}</h1>
 <p className="text-red-600 mb-4">{error}</p>
 <p className="text-ink-500 text-sm mb-8">
 {t('productDetailPage.productId')}: {productId} | {t('productDetailPage.productType')}: {type}
 </p>
 <div className="flex gap-4 justify-center">
 <button 
 onClick={() => window.location.reload()}
 className="bg-accent-500 text-white px-6 py-3 rounded-full font-semibold hover:bg-accent-600 transition-colors duration-300"
 >
 {t('productDetailPage.retry')}
 </button>
 <button 
 onClick={() => navigate('/')}
 className="bg-ink-500 text-white px-6 py-3 rounded-full font-semibold hover:bg-ink-600 transition-colors duration-300"
 >
 {t('productDetailPage.backToHome')}
 </button>
 </div>
 </div>
 </div>
 );
 }

 if (!product) {
 return (
 <div className="min-h-screen bg-cream-50 flex items-center justify-center">
 <div className="text-center max-w-md mx-auto px-6">
 <div className="w-24 h-24 bg-ink-100 rounded-full flex items-center justify-center mx-auto mb-6">
 <ShoppingCart className="w-12 h-12 text-ink-400" />
 </div>
 <h1 className="text-3xl font-bold text-ink-900 mb-4">{t('productDetailPage.productNotFound')}</h1>
 <p className="text-ink-600 mb-4">{t('productDetailPage.productNotFoundDesc')}</p>
 <p className="text-ink-500 text-sm mb-8">
 {t('productDetailPage.productId')}: {productId} | {t('productDetailPage.productType')}: {type}
 </p>
 <button 
 onClick={() => navigate('/')}
 className="bg-accent-500 text-white px-8 py-3 rounded-full font-semibold hover:bg-accent-600 transition-colors duration-300"
 >
 {t('productDetailPage.backToHome')}
 </button>
 </div>
 </div>
 );
 }

 return (
 <div className="min-h-screen bg-cream-50">

 {/* 主要内容区域 */}
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-16">
 <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
 
 {/* 左侧大区域 - 产品图片和信息 (占8列) */}
 <div className="lg:col-span-8">
 {/* 产品图片和基本信息的网格布局 */}
 <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
 
 {/* 产品图片展示 - 占3.5列 (约30%，为右侧留更多空间) */}
 <div className="lg:col-span-4 space-y-4">
 {/* 产品名称标题 - 方框包裹 */}
 <div className="mb-6">
 <div className="bg-cream-50 rounded-2xl p-6 shadow-lg border border-accent-200 relative overflow-hidden">
 {/* 装饰性背景元素 */}
 <div className="absolute -top-4 -right-4 w-16 h-16 bg-accent-200 rounded-full opacity-20 blur-xl"></div>

 <div className="relative z-10">
 <h1 className="text-2xl lg:text-3xl font-bold text-accent-600 leading-tight mb-2">
 {productData?.name || ''}
 </h1>
 
 {/* 产品说明文字 */}
 <p className="text-sm text-ink-600 leading-relaxed">
 {productData?.description || t('productDetailPage.defaultProduct.description')}
 </p>
 </div>
 </div>
 </div>
 
 {/* 主图显示区域 */}
 <div className="relative group">
 <div className="aspect-square bg-cream-100 rounded-xl overflow-hidden shadow-md">
 <img 
 src={productData?.images?.[selectedImage]}
 alt={productData?.name || ''}
 className="w-full h-full object-cover transition-all duration-700"
 onError={(e) => {
 // 如果图片加载失败，显示默认图标
 e.target.style.display = 'none';
 e.target.nextSibling.style.display = 'flex';
 }}
 />
 
 {/* 备用图标显示 */}
 <div className="w-full h-full bg-gradient-to-br from-accent-100 to-accent-200 flex items-center justify-center" style={{display: 'none'}}>
 <div className="text-center">
 <ShoppingCart className="w-20 h-20 text-accent-500 mx-auto mb-3" />
 <p className="text-accent-700 font-medium">{productData?.name || ''}</p>
 </div>
 </div>
 
 {/* 图片导航按钮 */}
 {productData?.images && productData.images.length > 1 && (
 <>
 <button 
 onClick={prevImage}
 className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-all duration-300 shadow-lg opacity-0 group-hover:opacity-100"
 >
 <ChevronLeft className="w-5 h-5 text-ink-700" />
 </button>
 
 <button 
 onClick={nextImage}
 className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-all duration-300 shadow-lg opacity-0 group-hover:opacity-100"
 >
 <ChevronRight className="w-5 h-5 text-ink-700" />
 </button>
 </>
 )}

 {/* 图片指示器 */}
 {productData?.images && productData.images.length > 1 && (
 <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
 {productData.images.map((_, index) => (
 <button
 key={index}
 onClick={() => setSelectedImage(index)}
 className={`h-2 rounded-full transition-all duration-300 ${
 selectedImage === index 
 ? 'bg-white w-8' 
 : 'bg-white/50 w-2 hover:bg-white/75'
 }`}
 />
 ))}
 </div>
 )}
 </div>
 </div>

 {/* 缩略图网格 */}
 {productData?.images && productData.images.length > 1 && (
 <div className="grid grid-cols-4 gap-2">
 {productData.images.slice(0, 8).map((image, index) => (
 <button
 key={index}
 onClick={() => setSelectedImage(index)}
 className={`aspect-square rounded-xl overflow-hidden border-2 transition-all duration-300 ${
 selectedImage === index 
 ? 'border-accent-500 shadow-lg' 
 : 'border-ink-200 hover:border-ink-300'
 }`}
 >
 <img 
 src={image} 
 alt={`${productData.name} ${index + 1}`}
 className="w-full h-full object-cover"
 />
 </button>
 ))}
 </div>
 )}

 {/* 产品特点展示 - 放在图片下方 */}
 {currentSku && (
 <div className="mt-6 p-4 bg-cream-100 rounded-xl">
 <h3 className="text-lg font-bold text-ink-900 mb-3">{t('productDetailPage.productFeatures.title')}</h3>
 <div className="flex flex-wrap gap-2">
 <span className="inline-flex items-center bg-accent-100 text-accent-700 px-3 py-1.5 rounded-full text-xs font-medium">
 <Monitor className="w-3 h-3 mr-1.5" />
 {currentSku.features.platform === 'web+app' ? t('productDetailPage.productFeatures.platform.webApp') : t('productDetailPage.productFeatures.platform.webOnly')}
 </span>
 <span className="inline-flex items-center bg-accent-100 text-accent-700 px-3 py-1.5 rounded-full text-xs font-medium">
 <Users className="w-3 h-3 mr-1.5" />
 {currentSku.features.users === 1 ? t('productDetailPage.productFeatures.users.exclusive') : t('productDetailPage.productFeatures.users.shared', { count: currentSku.features.users })}
 </span>
 <span className="inline-flex items-center bg-accent-100 text-accent-700 px-3 py-1.5 rounded-full text-xs font-medium">
 <Shield className="w-3 h-3 mr-1.5" />
 {currentSku.features.warranty === 'replacement' ? t('productDetailPage.productFeatures.warranty.replacement') : currentSku.features.warranty === 'monthly' ? t('productDetailPage.productFeatures.warranty.monthly') : currentSku.features.warranty}
 </span>
 {currentSku.features.passwordChangeable && (
 <span className="inline-flex items-center bg-accent-100 text-accent-700 px-3 py-1.5 rounded-full text-xs font-medium">
 <Settings className="w-3 h-3 mr-1.5" />
 {t('productDetailPage.productFeatures.passwordChangeable')}
 </span>
 )}
 {currentSku.features.privacy === 'private' && (
 <span className="inline-flex items-center bg-accent-100 text-accent-700 px-3 py-1.5 rounded-full text-xs font-medium">
 <Lock className="w-3 h-3 mr-1.5" />
 {t('productDetailPage.productFeatures.privacy.private')}
 </span>
 )}
 {currentSku.features.privacy === 'shared' && (
 <span className="inline-flex items-center bg-accent-100 text-accent-700 px-3 py-1.5 rounded-full text-xs font-medium">
 <Users className="w-3 h-3 mr-1.5" />
 {t('productDetailPage.productFeatures.privacy.shared')}
 </span>
 )}
 {currentSku.features.chatDeletable === false && (
 <span className="inline-flex items-center bg-red-100 text-red-700 px-3 py-1.5 rounded-full text-xs font-medium">
 <Minus className="w-3 h-3 mr-1.5" />
 {t('productDetailPage.productFeatures.chatDeletable.false')}
 </span>
 )}
 {currentSku.features.renewalOnly && (
 <span className="inline-flex items-center bg-accent-100 text-accent-700 px-3 py-1.5 rounded-full text-xs font-medium">
 <RefreshCw className="w-3 h-3 mr-1.5" />
 {t('productDetailPage.productFeatures.renewalOnly')}
 </span>
 )}
 </div>
 </div>
 )}

 {/* 产品类型特性提示卡片 - 移到左侧图片下方 */}
 {selectedProductType && (
 <div className="mt-6 bg-cream-50 rounded-xl p-5 shadow-lg border border-accent-200">
 <div className="flex items-start gap-3">
 <div className="flex-shrink-0">
 {selectedProductType === 'official' && (
 <div className="w-10 h-10 bg-accent-500 rounded-lg flex items-center justify-center shadow-lg">
 <Crown className="w-5 h-5 text-white" />
 </div>
 )}
 {selectedProductType === 'official-shared' && (
 <div className="w-10 h-10 bg-accent-500 rounded-lg flex items-center justify-center shadow-lg">
 <Users className="w-5 h-5 text-white" />
 </div>
 )}
 {selectedProductType === 'shared' && (
 <div className="w-10 h-10 bg-accent-500 rounded-lg flex items-center justify-center shadow-lg">
 <Globe className="w-5 h-5 text-white" />
 </div>
 )}
 {selectedProductType === 'official-renewal' && (
 <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center shadow-lg">
 <RefreshCw className="w-5 h-5 text-white" />
 </div>
 )}
 </div>
 <div className="flex-1">
 <h3 className="text-base font-bold text-ink-900 mb-2">
 {selectedProductType === 'official' && t('productDetailPage.productTypes.official.title')}
 {selectedProductType === 'official-shared' && t('productDetailPage.productTypes.officialShared.title')}
 {selectedProductType === 'shared' && t('productDetailPage.productTypes.shared.title')}
 {selectedProductType === 'official-renewal' && t('productDetailPage.productTypes.officialRenewal.title')}
 </h3>
 <div className="space-y-1.5">
 {selectedProductType === 'official' && (
 <>
 <p className="text-xs text-ink-600 flex items-start gap-2">
 <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
 <span>{t('productDetailPage.productTypes.official.features.privacy')}</span>
 </p>
 <p className="text-xs text-ink-600 flex items-start gap-2">
 <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
 <span>{t('productDetailPage.productTypes.official.features.passwordControl')}</span>
 </p>
 <p className="text-xs text-ink-600 flex items-start gap-2">
 <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
 <span>{t('productDetailPage.productTypes.official.features.dualLogin')}</span>
 </p>
 </>
 )}
 {selectedProductType === 'official-shared' && (
 <>
 <p className="text-xs text-ink-600 flex items-start gap-2">
 <Shield className="w-3.5 h-3.5 text-accent-500 flex-shrink-0 mt-0.5" />
 <span>{t('productDetailPage.productTypes.officialShared.features.costEffective')}</span>
 </p>
 <p className="text-xs text-ink-600 flex items-start gap-2">
 <Shield className="w-3.5 h-3.5 text-accent-500 flex-shrink-0 mt-0.5" />
 <span>{t('productDetailPage.productTypes.officialShared.features.noPasswordChange')}</span>
 </p>
 <p className="text-xs text-ink-600 flex items-start gap-2">
 <Shield className="w-3.5 h-3.5 text-accent-500 flex-shrink-0 mt-0.5" />
 <span>{t('productDetailPage.productTypes.officialShared.features.sharedConversations')}</span>
 </p>
 </>
 )}
 {selectedProductType === 'shared' && (
 <>
 <p className="text-xs text-ink-600 flex items-start gap-2">
 <Check className="w-3.5 h-3.5 text-accent-500 flex-shrink-0 mt-0.5" />
 <span>{t('productDetailPage.productTypes.shared.features.noAccount')}</span>
 </p>
 <p className="text-xs text-ink-600 flex items-start gap-2">
 <Check className="w-3.5 h-3.5 text-accent-500 flex-shrink-0 mt-0.5" />
 <span>{t('productDetailPage.productTypes.shared.features.privateConversations')}</span>
 </p>
 <p className="text-xs text-ink-600 flex items-start gap-2">
 <Check className="w-3.5 h-3.5 text-accent-500 flex-shrink-0 mt-0.5" />
 <span>{t('productDetailPage.productTypes.shared.features.stableService')}</span>
 </p>
 </>
 )}
 </div>
 </div>
 </div>
 </div>
 )}

 </div>

 {/* 产品信息 - 占8列 (约70%) - Sunset Glow Design */}
 <div className="lg:col-span-8 relative">
 {/* 装饰性背景光晕 */}
 <div className="absolute -top-10 -left-10 w-32 h-32 bg-accent-200 rounded-full opacity-10 blur-3xl"></div>
 <div className="absolute top-20 -right-10 w-24 h-24 bg-accent-200 rounded-full opacity-15 blur-2xl"></div>
 
 <div className="space-y-8 relative z-10">

 {/* 产品类型选择器 - Sunset Glow Card System */}
 <div className="bg-cream-50 rounded-xl p-8 shadow-xl border border-accent-200 relative overflow-hidden">
 {/* 装饰性背景元素 */}
 <div className="absolute -top-12 -right-12 w-24 h-24 bg-accent-200 rounded-full opacity-20 blur-2xl"></div>
 <div className="absolute -bottom-8 -left-8 w-20 h-20 bg-accent-200 rounded-full opacity-15 blur-2xl"></div>
 
 <div className="relative z-10 space-y-6">
 <div className="flex items-center justify-between mb-6">
 <div className="flex items-center gap-3">
 <div className="w-10 h-10 bg-accent-500 rounded-full flex items-center justify-center shadow-lg">
 <Crown className="w-5 h-5 text-white" />
 </div>
 <h2 className="text-2xl font-bold text-ink-900">{t('productDetailPage.productSelector.productType.title')}</h2>
 </div>
 
 {/* 展开/折叠按钮 */}
 <button
 onClick={() => setIsProductTypeExpanded(!isProductTypeExpanded)}
 className="flex items-center gap-2 px-4 py-2 bg-cream-50 rounded-xl border border-accent-200 hover:border-accent-400 hover:bg-accent-50 transition-all duration-300 shadow-sm"
 >
 <span className="text-sm text-ink-600">
 {isProductTypeExpanded ? t('productDetailPage.productSelector.productType.collapse') : t('productDetailPage.productSelector.productType.expand')}
 </span>
 <ChevronRight className={`w-4 h-4 text-ink-500 transition-transform duration-300 ${
 isProductTypeExpanded ? 'rotate-90' : ''
 }`} />
 </button>
 </div>
 
 {/* 选项区域 */}
 <div className="space-y-2">
 {Object.entries(SKU_DIMENSIONS.PRODUCT_TYPES).map(([key, value]) => {
 const isSelected = selectedProductType === value;
 const shouldShow = isProductTypeExpanded || isSelected;
 
 if (!shouldShow) return null;
 
 const getTypeInfo = (type) => {
 switch(type) {
 case 'official':
 return { icon: Crown, label: t('productDetailPage.productSelector.productType.types.official.name'), color: 'blue' };
 case 'official-renewal':
 return { icon: RefreshCw, label: t('productDetailPage.productSelector.productType.types.officialRenewal.name'), color: 'green' };
 case 'official-shared':
 return { icon: Users, label: t('productDetailPage.productSelector.productType.types.officialShared.name'), color: 'purple' };
 case 'shared':
 return { icon: Globe, label: t('productDetailPage.productSelector.productType.types.shared.name', '网页共享托管账号'), color: 'purple' };
 case 'monkeyai':
 return { icon: Sparkles, label: 'MonkeyAI', color: 'indigo' };
 default:
 return { icon: Crown, label: type, color: 'gray' };
 }
 };
 
 const typeInfo = getTypeInfo(value);
 const IconComponent = typeInfo.icon;
 
 const getColorClasses = (color, isSelected) => {
 if (isSelected) {
 switch(color) {
 case 'blue': return 'bg-accent-500 text-white shadow-lg';
 case 'green': return 'bg-green-500 text-white shadow-lg';
 case 'purple': return 'bg-accent-500 text-white shadow-lg';
 case 'purple': return 'bg-accent-500 text-white shadow-lg';
 case 'indigo': return 'bg-accent-500 text-white shadow-lg';
 default: return 'bg-ink-500 text-white shadow-lg';
 }
 } else {
 switch(color) {
 case 'blue': return 'bg-accent-100 hover:bg-accent-200';
 case 'green': return 'bg-green-100 hover:bg-green-200';
 case 'purple': return 'bg-accent-100 hover:bg-accent-200';
 case 'purple': return 'bg-accent-100 hover:bg-accent-200';
 case 'indigo': return 'bg-accent-100 hover:bg-accent-200';
 default: return 'bg-cream-100 hover:bg-ink-100';
 }
 }
 };

 const getIconColorClass = (color, isSelected) => {
 if (isSelected) return 'text-white';
 switch(color) {
 case 'blue': return 'text-accent-600';
 case 'green': return 'text-green-600';
 case 'purple': return 'text-accent-600';
 case 'purple': return 'text-accent-600';
 case 'indigo': return 'text-accent-600';
 default: return 'text-ink-600';
 }
 };

 const getTextColorClass = (color, isSelected) => {
 if (isSelected) return 'text-white';
 switch(color) {
 case 'blue': return 'text-accent-700';
 case 'green': return 'text-green-700';
 case 'purple': return 'text-accent-700';
 case 'purple': return 'text-accent-700';
 case 'indigo': return 'text-accent-700';
 default: return 'text-ink-700';
 }
 };

 const getTypeDescription = (type) => {
 switch(type) {
 case 'official':
 return t('productDetailPage.productSelector.productType.types.official.description');
 case 'official-renewal':
 return t('productDetailPage.productSelector.productType.types.officialRenewal.description');
 case 'official-shared':
 return t('productDetailPage.productSelector.productType.types.officialShared.description');
 case 'shared':
 return t('productDetailPage.productSelector.productType.types.shared.description', '网页端托管账号，快速便捷使用');
 case 'monkeyai':
 return t('productDetailPage.productSelector.productType.types.monkeyai.description', '专属AI助手，智能化体验');
 default:
 return t('productDetailPage.productSelector.productType.types.default', '选择适合您的产品类型');
 }
 };
 
 const handleProductTypeSelect = (newValue) => {
 setSelectedProductType(newValue);
 setSelectedImage(0); // 重置为第一张图片
 if (isProductTypeExpanded) {
 setIsProductTypeExpanded(false);
 }
 };
 
 return (
 <div 
 key={value}
 onClick={() => handleProductTypeSelect(value)}
 className={`group p-4 rounded-xl border-2 transition-all duration-300 hover:shadow-lg relative overflow-hidden cursor-pointer ${
 isSelected 
 ? 'border-accent-500 bg-accent-100 shadow-lg'
 : 'border-ink-200 hover:border-accent-300 bg-cream-50'
 }`}
 >
 {/* 选中时的装饰背景 */}
 {isSelected && (
 <div className="absolute -top-8 -right-8 w-16 h-16 bg-accent-200 rounded-full opacity-20 blur-xl"></div>
 )}
 
 <div className="relative z-10 flex items-center gap-4">
 {/* 左侧图标区域 */}
 <div className="flex items-center gap-3">
 <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
 isSelected
 ? 'border-accent-500 bg-accent-500 shadow-lg'
 : 'border-ink-300 group-hover:border-accent-400'
 }`}>
 {isSelected && (
 <Check className="w-4 h-4 text-white" />
 )}
 </div>

 <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${
 isSelected
 ? 'bg-accent-500 shadow-lg'
 : 'bg-cream-100 hover:bg-accent-100'
 }`}>
 <IconComponent className={`w-6 h-6 ${
 isSelected ? 'text-white' : 'text-ink-600 group-hover:text-accent-600'
 }`} />
 </div>
 </div>
 
 {/* 右侧文字内容 */}
 <div className="flex-1">
 <h3 className={`font-bold text-lg mb-1 ${
 isSelected ? 'text-ink-900' : 'text-ink-800 group-hover:text-accent-600'
 }`}>
 {typeInfo.label}
 </h3>
 
 <p className="text-sm text-ink-500 leading-relaxed">
 {getTypeDescription(value)}
 </p>
 </div>
 </div>
 </div>
 );
 })}
 </div>
 </div>
 </div>

 {/* 用户配置选择器 - Sunset Glow Design */}
 {availableUserConfigs.length > 0 && (
 <div className="bg-cream-50 rounded-xl p-8 shadow-xl border border-accent-200 relative overflow-hidden">
 {/* 装饰性背景元素 */}
 <div className="absolute -top-8 -left-8 w-20 h-20 bg-accent-200 rounded-full opacity-15 blur-2xl"></div>
 
 <div className="relative z-10 space-y-6">
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-3">
 <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
 <Users className="w-5 h-5 text-white" />
 </div>
 <h2 className="text-2xl font-bold text-ink-900">{t('productDetailPage.productSelector.userConfig.title')}</h2>
 </div>
 
 {/* 展开/折叠按钮 - 共享类型不显示 */}
 {availableUserConfigs.length > 1 && 
 selectedProductType !== 'official-shared' && 
 selectedProductType !== 'shared' && (
 <button
 onClick={() => setIsUserConfigExpanded(!isUserConfigExpanded)}
 className="flex items-center gap-2 px-4 py-2 bg-cream-50 rounded-xl border border-green-200 hover:border-green-400 hover:bg-green-50 transition-all duration-300 shadow-sm"
 >
 <span className="text-sm text-ink-600">
 {isUserConfigExpanded ? '收起选项' : '展开选项'}
 </span>
 <ChevronRight className={`w-4 h-4 text-ink-500 transition-transform duration-300 ${
 isUserConfigExpanded ? 'rotate-90' : ''
 }`} />
 </button>
 )}
 </div>
 
 {/* 选项区域 */}
 <div className="space-y-2">
 {availableUserConfigs.map((config) => {
 const isSelected = selectedUserConfig === config;
 // 如果是共享类型的产品，始终显示所有选项
 const isSharedType = selectedProductType === 'official-shared' || selectedProductType === 'shared';
 const shouldShow = isSharedType || isUserConfigExpanded || isSelected || availableUserConfigs.length === 1;
 
 if (!shouldShow) return null;
 
 const getConfigLabel = (config) => {
 switch(config) {
 case '1user': return t('productDetailPage.productSelector.userConfig.configs.1user.name');
 case '3users': return t('productDetailPage.productSelector.userConfig.configs.3users.name');
 case '6users': return t('productDetailPage.productSelector.userConfig.configs.6users.name');
 default: return config;
 }
 };

 const getConfigDescription = (config) => {
 switch(config) {
 case '1user': return t('productDetailPage.productSelector.userConfig.configs.1user.description');
 case '3users': return t('productDetailPage.productSelector.userConfig.configs.3users.description');
 case '6users': return t('productDetailPage.productSelector.userConfig.configs.6users.description');
 default: return t('productDetailPage.productSelector.userConfig.configs.default', '选择用户数量');
 }
 };
 
 const handleUserConfigSelect = (newConfig) => {
 setSelectedUserConfig(newConfig);
 setSelectedImage(0); // 重置为第一张图片
 if (isUserConfigExpanded) {
 setIsUserConfigExpanded(false);
 }
 };
 
 return (
 <div 
 key={config}
 onClick={() => handleUserConfigSelect(config)}
 className={`group p-4 rounded-xl border-2 transition-all duration-300 hover:shadow-lg relative overflow-hidden cursor-pointer ${
 isSelected 
 ? 'border-green-500 bg-green-100 shadow-lg'
 : 'border-ink-200 hover:border-green-300 bg-cream-50'
 }`}
 >
 {/* 选中时的装饰背景 */}
 {isSelected && (
 <div className="absolute -top-8 -right-8 w-16 h-16 bg-green-200 rounded-full opacity-20 blur-xl"></div>
 )}
 
 <div className="relative z-10 flex items-center gap-4">
 {/* 左侧图标区域 */}
 <div className="flex items-center gap-3">
 <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
 isSelected 
 ? 'border-green-500 bg-green-500 shadow-lg'
 : 'border-ink-300 group-hover:border-green-400'
 }`}>
 {isSelected && (
 <Check className="w-4 h-4 text-white" />
 )}
 </div>
 
 <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${
 isSelected 
 ? 'bg-green-500 shadow-lg'
 : 'bg-cream-100 hover:bg-green-100'
 }`}>
 <Users className={`w-6 h-6 ${
 isSelected ? 'text-white' : 'text-ink-600 group-hover:text-green-600'
 }`} />
 </div>
 </div>
 
 {/* 右侧文字内容 */}
 <div className="flex-1">
 <h3 className={`font-bold text-lg mb-1 ${
 isSelected ? 'text-ink-900' : 'text-ink-800 group-hover:text-green-600'
 }`}>
 {getConfigLabel(config)}
 </h3>
 
 <p className="text-sm text-ink-500 leading-relaxed">
 {getConfigDescription(config)}
 </p>
 </div>
 </div>
 </div>
 );
 })}
 </div>
 </div>
 </div>
 )}

 {/* 套餐选择 - Sunset Glow Card System */}
 <div className="bg-cream-50 rounded-xl p-8 shadow-xl border border-accent-200 relative overflow-hidden">
 {/* 装饰性背景元素 */}
 <div className="absolute -top-16 -right-16 w-32 h-32 bg-accent-200 rounded-full opacity-20 blur-3xl"></div>
 <div className="absolute -bottom-12 -left-12 w-28 h-28 bg-accent-200 rounded-full opacity-15 blur-2xl"></div>
 
 <div className="relative z-10 space-y-8">
 <div className="flex items-center gap-3">
 <div className="w-10 h-10 bg-accent-500 rounded-full flex items-center justify-center shadow-lg">
 <Clock className="w-5 h-5 text-white" />
 </div>
 <h2 className="text-2xl font-bold text-ink-900">{t('productDetailPage.productSelector.duration.title')}</h2>
 </div>
 
 <div className="space-y-4 relative overflow-visible pt-2 pl-2">
 {availableDurations.map((durationInfo, index) => {
 const isSelected = selectedDuration === durationInfo.duration;
 const months = durationInfo.duration === '12months' ? 12 : durationInfo.duration === '3months' ? 3 : 1;
 const monthlyPrice = (durationInfo.price / months).toFixed(2);
 const isPopular = months === 3;
 
 const getDurationLabel = (duration) => {
 switch(duration) {
 case '1month': return t('productDetailPage.productSelector.duration.options.1month.name');
 case '3months': return t('productDetailPage.productSelector.duration.options.3months.name');
 case '12months': return t('productDetailPage.productSelector.duration.options.12months.name');
 default: return duration;
 }
 };
 
 return (
 <div
 key={durationInfo.duration}
 onClick={() => setSelectedDuration(durationInfo.duration)}
 className={`group relative bg-cream-50 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:shadow-lg p-4 ${
 isSelected
 ? 'border-accent-500 shadow-xl bg-accent-50'
 : 'border-ink-200 hover:border-accent-300'
 }`}
 >
 {/* 推荐标签 - 移到左上角避免遮挡价格 */}
 {isPopular && (
 <div className="absolute -top-1 -left-1 z-[100]">
 <div className="bg-accent-500 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-xl border-2 border-white transform -rotate-12 animate-pulse relative">
 <div className="flex items-center gap-1 whitespace-nowrap">
 <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
 <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
 </svg>
 <span>{t('productDetailPage.productSelector.duration.recommended')}</span>
 </div>
 </div>
 </div>
 )}

 {/* 选中时的装饰背景 */}
 {isSelected && (
 <div className="absolute -top-4 -right-4 w-16 h-16 bg-accent-200 rounded-full opacity-20 blur-xl"></div>
 )}
 
 <div className="flex items-center justify-between">
 {/* 左侧 - 选中标记和套餐信息 */}
 <div className="flex items-center gap-3">
 <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
 isSelected 
 ? 'border-accent-500 bg-accent-500 shadow-lg'
 : 'border-ink-300 group-hover:border-accent-400'
 }`}>
 {isSelected && (
 <Check className="w-4 h-4 text-white" />
 )}
 </div>

 <div>
 <h3 className={`text-lg font-bold ${
 isSelected ? 'text-ink-900' : 'text-ink-800 group-hover:text-accent-600'
 }`}>
 {getDurationLabel(durationInfo.duration)}
 </h3>
 
 {/* 月均价格提示 */}
 {months > 1 && (
 <p className="text-sm text-green-600 font-medium">
 {t('productDetailPage.productSelector.duration.monthlyAverage')} ${monthlyPrice}
 </p>
 )}
 </div>
 </div>

 {/* 右侧 - 价格信息 */}
 <div className="text-right">
 {/* 原价显示 */}
 {durationInfo.originalPrice > durationInfo.price && (
 <div className="text-sm text-ink-400 line-through mb-1">
 ${durationInfo.originalPrice}
 </div>
 )}
 
 {/* 当前价格 */}
 <div className="flex items-baseline justify-end gap-1">
 <span className="text-sm font-medium text-ink-600">$</span>
 <span className={`text-2xl font-bold ${
 isSelected
 ? 'text-accent-600'
 : 'text-ink-800 group-hover:text-accent-600'
 }`}>
 {durationInfo.price}
 </span>
 </div>
 </div>
 </div>
 </div>
 );
 })}
 </div>
 </div>
 </div>

 </div>
 </div>
 </div>


 </div>

 {/* 右侧 - 购买信息和说明 (占4列) */}
 <div className="lg:col-span-4 space-y-6">
 {currentSku && (
 <>
 {/* 购买卡片 - 优化设计 */}
 <div className="bg-cream-50 rounded-xl shadow-md border border-accent-200 p-8 space-y-6 relative overflow-hidden">
 {/* 装饰性背景元素 */}
 <div className="absolute -top-20 -right-20 w-40 h-40 bg-accent-200 rounded-full opacity-20 blur-3xl"></div>
 <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-accent-200 rounded-full opacity-20 blur-3xl"></div>
 
 {/* 价格显示 - 更突出的设计 */}
 <div className="relative z-10 pb-6 border-b-2 border-accent-200">
 <div className="flex items-center justify-between mb-3">
 <div>
 <div className="flex items-baseline gap-3 mb-2">
 <span className="text-5xl font-bold text-accent-600">
 {currentSku.currency}{currentSku.price}
 </span>
 {currentSku.originalPrice > currentSku.price && (
 <span className="text-xl text-ink-400 line-through">
 {currentSku.currency}{currentSku.originalPrice}
 </span>
 )}
 </div>
 {/* 显示月均价格 - 更醒目的样式 */}
 {(() => {
 const months = selectedDuration === '12months' ? 12 : selectedDuration === '3months' ? 3 : 1;
 const monthlyPrice = (currentSku.price / months).toFixed(2);
 return months > 1 ? (
 <div className="inline-flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-full shadow-lg">
 <Sparkles className="w-4 h-4" />
 <span className="text-sm font-bold">{t('productDetailPage.equivalentTo')} {currentSku.currency}{monthlyPrice}{t('productDetailPage.perMonth')}</span>
 </div>
 ) : null;
 })()}
 </div>

 </div>
 </div>
 
 {/* 库存状态 - 更醒目的设计 */}
 <div className="bg-white rounded-xl p-4 shadow-sm border border-ink-100">
 <div className="flex items-center justify-between">
 <span className="text-sm text-ink-700 font-semibold flex items-center gap-2">
 <Zap className="w-4 h-4 text-accent-500" />
 {t('productDetailPage.stockStatus')}
 </span>
 {stockLoading ? (
 <div className="flex items-center gap-2 bg-accent-100 px-3 py-1 rounded-full">
 <div className="w-2 h-2 bg-accent-500 rounded-full animate-pulse"></div>
 <span className="text-sm text-accent-600 font-semibold">{t('productDetailPage.checking')}</span>
 </div>
 ) : realStockStatus === true ? (
 <div className="flex items-center gap-2 bg-green-100 px-3 py-1 rounded-full">
 <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
 <span className="text-sm text-green-600 font-semibold">{t('productDetailPage.inStock')}</span>
 </div>
 ) : realStockStatus === false ? (
 <div className="flex items-center gap-2 bg-red-100 px-3 py-1 rounded-full">
 <div className="w-2 h-2 bg-red-500 rounded-full"></div>
 <span className="text-sm text-red-600 font-semibold">{t('productDetailPage.outOfStock')}</span>
 </div>
 ) : (
 <div className="flex items-center gap-2 bg-cream-100 px-3 py-1 rounded-full">
 <div className="w-2 h-2 bg-ink-300 rounded-full"></div>
 <span className="text-sm text-ink-500 font-semibold">{t('productDetailPage.unknown')}</span>
 </div>
 )}
 </div>
 </div>

 {/* 数量显示（固定为1）- 优化样式 */}
 <div className="bg-white rounded-xl p-4 shadow-sm border border-ink-100">
 <div className="flex items-center justify-between">
 <span className="text-sm text-ink-700 font-semibold flex items-center gap-2">
 <ShoppingCart className="w-4 h-4 text-accent-500" />
 {t('productDetailPage.purchaseQuantity')}
 </span>
 <div className="flex items-center bg-accent-100 px-4 py-2 rounded-lg">
 <span className="font-bold text-accent-600">
 1
 </span>
 </div>
 </div>
 </div>

 {/* 邮箱输入 - 优化样式 */}
 <div className="space-y-3">
 <label className="block text-sm text-ink-700 font-semibold flex items-center gap-2">
 <DollarSign className="w-4 h-4 text-accent-500" />
 {isLoggedIn ? t('productDetailPage.purchaseAccount') : t('productDetailPage.purchaseEmail')} <span className="text-red-500">*</span>
 </label>
 <div className="relative">
 <input
 ref={emailInputRef}
 type="email"
 value={email}
 onChange={(e) => setEmail(e.target.value)}
 placeholder={isLoggedIn ? t('productDetailPage.loggedInAccountEmail') : t('productDetailPage.enterEmailAddress')}
 className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-300 text-sm font-medium ${
 isLoggedIn 
 ? 'border-accent-200 bg-accent-50 text-ink-600 cursor-not-allowed' 
 : emailShaking
 ? 'border-red-500 bg-red-50 focus:border-red-500 focus:outline-none focus:ring-4 focus:ring-red-100'
 : 'border-accent-200 focus:border-accent-500 focus:outline-none focus:ring-4 focus:ring-accent-100 bg-white'
 }`}
 disabled={isLoggedIn}
 required
 />
 {isLoggedIn && (
 <div className="absolute right-3 top-1/2 -translate-y-1/2">
 <Check className="w-5 h-5 text-green-500" />
 </div>
 )}
 </div>
 {/* 邮箱提醒信息 */}
 {emailShaking && !isLoggedIn && (
 <div className="p-3 bg-red-50 border border-red-200 rounded-lg animate-pulse">
 <p className="text-red-600 text-xs font-semibold flex items-center gap-2">
 <span className="text-red-500">⚠️</span>
 {t('productDetailPage.alert.enterValidEmail')}
 </p>
 </div>
 )}
 
 <p className="text-xs text-ink-500 flex items-center gap-1">
 <Shield className="w-3 h-3" />
 {isLoggedIn 
 ? t('productDetailPage.useLoggedInAccount') 
 : t('productDetailPage.orderInfoSentToEmail')
 }
 </p>
 </div>

 {/* 购买按钮 - 始终高亮设计 */}
 <div className="pt-4">
 <button 
 ref={buyButtonRef}
 disabled={realStockStatus === false || stockLoading}
 className={`relative w-full bg-accent-500 hover:bg-accent-600 text-white py-5 px-8 rounded-2xl font-bold text-lg shadow-md flex items-center justify-center gap-3 transition-all duration-300 overflow-hidden group ${
 realStockStatus !== false && !stockLoading
 ? '' 
 : 'disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none'
 }`}
 onClick={() => {
 // 邮箱格式验证
 const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
 if (!email || !emailRegex.test(email)) {
 // 触发邮箱输入框抖动而不是弹窗
 triggerEmailShake();
 return;
 }

 // 验证是否选择了SKU
 if (!currentSku) {
 alert('请选择产品类型和套餐');
 return;
 }

 // 再次检查库存状态
 if (realStockStatus === false) {
 alert(t('productDetailPage.alert.productOutOfStock'));
 return;
 }

 // 检查是否是续费产品，如果是则显示确认弹窗
 if (selectedProductType === 'official-renewal') {
 setShowRenewalModal(true);
 return;
 }

 // 普通产品直接跳转购买
 proceedToPurchase();
 }}
 >
 {/* 动态背景效果 */}
 <div className="absolute inset-0 bg-accent-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
 
 {/* 按钮内容 */}
 <div className="relative z-10 flex items-center gap-3">
 <ShoppingCart className={`w-6 h-6 ${realStockStatus !== false && !stockLoading ? 'group-hover:rotate-12 transition-transform duration-300' : ''}`} />
 <span className="flex items-center gap-2">
 {stockLoading ? t('productDetailPage.checkingStock') : realStockStatus === false ? t('productDetailPage.temporarilyOutOfStock') : (
 <>
 {t('productDetailPage.buyNow')}
 <span className="text-2xl group-hover:translate-x-1 transition-transform duration-300">→</span>
 </>
 )}
 </span>
 </div>
 
 {/* 闪光效果 - 始终显示 */}
 {realStockStatus !== false && !stockLoading && (
 <div className="absolute -inset-1 bg-accent-400 opacity-30 blur-lg group-hover:opacity-50 transition-opacity duration-300"></div>
 )}
 </button>
 
 {/* 紧急提示 */}
 {realStockStatus === true && !stockLoading && (
 <div className="mt-3 flex items-center justify-center gap-2 text-accent-600 animate-pulse">
 <Clock className="w-4 h-4" />
 <span className="text-xs font-semibold">{t('productDetailPage.limitedTimeOffer')}</span>
 </div>
 )}
 </div>

 {/* 安全提示 - 更突出的设计 */}
 <div className="pt-2 flex items-center justify-center gap-4 text-xs text-ink-600">
 <div className="flex items-center gap-1">
 <Shield className="w-4 h-4 text-green-500" />
 <span>{t('productDetailPage.securePayment')}</span>
 </div>
 <div className="w-1 h-1 bg-ink-300 rounded-full"></div>
 <div className="flex items-center gap-1">
 <Lock className="w-4 h-4 text-accent-500" />
 <span>{t('productDetailPage.sslEncryption')}</span>
 </div>
 <div className="w-1 h-1 bg-ink-300 rounded-full"></div>
 <div className="flex items-center gap-1">
 <RefreshCw className="w-4 h-4 text-accent-500" />
 <span>{t('productDetailPage.afterSalesGuarantee')}</span>
 </div>
 </div>
 </div>

 </>
 )}
 </div>
 </div>
 </div>

 {/* Hero Section */}
 <ProductDetailHero />
 
 {/* 关于产品 - 全屏宽度 */}
 <section className="py-16 bg-cream-50">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="text-center mb-12">
 <div className="inline-flex items-center gap-3 mb-6">
 <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center">
 <Monitor className="w-6 h-6 text-accent-600" />
 </div>
 <h2 className="text-3xl lg:text-4xl font-bold text-ink-900">{t('productDetailPage.aboutProduct.title')}</h2>
 </div>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
 {/* 动态渲染关于产品的内容 */}
 {(Array.isArray(productData?.aboutProduct) ? productData.aboutProduct : []).map((item, index) => (
 <div key={index} className="bg-cream-50 rounded-2xl p-6 shadow-lg border border-accent-100 hover:shadow-xl transition-all duration-300">
 <div className="flex items-start gap-4">
 <div className="flex-shrink-0 w-8 h-8 bg-accent-500 rounded-full flex items-center justify-center mt-1">
 <Check className="w-5 h-5 text-white" />
 </div>
 <div className="flex-1">
 <p className="text-ink-700 leading-relaxed text-lg">{item}</p>
 </div>
 </div>
 </div>
 ))}

 {/* 如果没有配置数据，显示默认内容 */}
 {(!productData?.aboutProduct || productData.aboutProduct.length === 0) && (
 <div className="md:col-span-2 lg:col-span-3">
 <div className="bg-cream-50 rounded-2xl p-8 shadow-lg border border-accent-100 text-center">
 <div className="flex items-center justify-center gap-4 mb-4">
 <div className="w-8 h-8 bg-accent-500 rounded-full flex items-center justify-center">
 <Check className="w-5 h-5 text-white" />
 </div>
 <p className="text-ink-700 leading-relaxed text-lg">{t('productDetailPage.aboutProduct.defaultContent')}</p>
 </div>
 </div>
 </div>
 )}
 </div>
 </div>
 </section>

 {/* 购买后获得 - 全屏宽度 */}
 <section className="py-16 bg-cream-50">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="text-center mb-12">
 <div className="inline-flex items-center gap-3 mb-6">
 <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
 <Award className="w-6 h-6 text-green-600" />
 </div>
 <h2 className="text-3xl lg:text-4xl font-bold text-ink-900">{t('productDetailPage.afterPurchase.title')}</h2>
 </div>
 </div>
 
 <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
 {/* 动态渲染购买后获得的内容或显示默认内容 */}
 {(Array.isArray(productData?.afterPurchase) && productData.afterPurchase.length > 0) ? (
 productData.afterPurchase.map((item, index) => (
 <div key={index} className="bg-cream-50 rounded-2xl p-6 shadow-lg border border-green-100 hover:shadow-xl transition-all duration-300">
 <div className="flex items-start gap-4">
 <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mt-1">
 <Check className="w-5 h-5 text-white" />
 </div>
 <div className="flex-1">
 <p className="text-ink-700 leading-relaxed text-lg">{item}</p>
 </div>
 </div>
 </div>
 ))
 ) : (
 <>
 <div className="bg-cream-50 rounded-2xl p-6 shadow-lg border border-green-100 hover:shadow-xl transition-all duration-300">
 <div className="flex items-start gap-4">
 <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mt-1">
 <Check className="w-5 h-5 text-white" />
 </div>
 <div className="flex-1">
 <p className="text-ink-700 leading-relaxed text-lg">{t('productDetailPage.afterPurchase.accountInfo')}</p>
 </div>
 </div>
 </div>
 <div className="bg-cream-50 rounded-2xl p-6 shadow-lg border border-green-100 hover:shadow-xl transition-all duration-300">
 <div className="flex items-start gap-4">
 <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mt-1">
 <Check className="w-5 h-5 text-white" />
 </div>
 <div className="flex-1">
 <p className="text-ink-700 leading-relaxed text-lg">{t('productDetailPage.afterPurchase.usageGuide')}</p>
 </div>
 </div>
 </div>
 <div className="bg-cream-50 rounded-2xl p-6 shadow-lg border border-green-100 hover:shadow-xl transition-all duration-300">
 <div className="flex items-start gap-4">
 <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mt-1">
 <Check className="w-5 h-5 text-white" />
 </div>
 <div className="flex-1">
 <p className="text-ink-700 leading-relaxed text-lg">{t('productDetailPage.afterPurchase.customerSupport')}</p>
 </div>
 </div>
 </div>
 </>
 )}
 </div>
 </div>
 </section>

 {/* 注意事项 - 全屏宽度 */}
 <section className="py-16 bg-cream-50">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="text-center mb-12">
 <div className="inline-flex items-center gap-3 mb-6">
 <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center">
 <Shield className="w-6 h-6 text-accent-600" />
 </div>
 <h2 className="text-3xl lg:text-4xl font-bold text-ink-900">{t('productDetailPage.importantNotes.title')}</h2>
 </div>
 </div>
 
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
 {/* 动态渲染注意事项的内容 */}
 {(Array.isArray(productData?.importantNotes) && productData.importantNotes.length > 0) ? (
 productData.importantNotes.map((item, index) => {
 // 分割问题和答案
 const lines = item.split('\n');
 const question = lines[0];
 const answer = lines.slice(1).join('\n');
 
 return (
 <div key={index} className="bg-cream-50 rounded-2xl p-6 shadow-lg border border-accent-100 hover:shadow-xl transition-all duration-300">
 <div className="flex items-start gap-4">
 <div className="flex-shrink-0 w-8 h-8 bg-accent-500 rounded-full flex items-center justify-center mt-1">
 <span className="text-white text-sm font-bold">!</span>
 </div>
 <div className="flex-1">
 <h4 className="font-semibold text-ink-900 mb-2 text-lg">{question}</h4>
 <p className="text-ink-600 leading-relaxed whitespace-pre-line">{answer}</p>
 </div>
 </div>
 </div>
 );
 })
 ) : (
 // 显示FAQ作为默认内容
 (getProductFAQ(productData?.id || type) || []).map((faq, index) => (
 <div key={index} className="bg-cream-50 rounded-2xl p-6 shadow-lg border border-accent-100 hover:shadow-xl transition-all duration-300">
 <div className="flex items-start gap-4">
 <div className="flex-shrink-0 w-8 h-8 bg-accent-500 rounded-full flex items-center justify-center mt-1">
 <span className="text-white text-sm font-bold">!</span>
 </div>
 <div className="flex-1">
 <h4 className="font-semibold text-ink-900 mb-2 text-lg">{faq.question}</h4>
 <p className="text-ink-600 leading-relaxed">{faq.answer}</p>
 </div>
 </div>
 </div>
 ))
 )}
 </div>
 
 {/* 联系客服 */}
 <div className="mt-12 text-center">
 <div className="bg-cream-50 rounded-2xl p-6 shadow-lg border border-accent-100 inline-block">
 <p className="text-ink-600 text-lg">
 {t('productDetailPage.haveOtherQuestions')}
 <button 
 onClick={() => navigate('/support')}
 className="text-accent-500 hover:text-accent-600 font-medium ml-2 transition-colors duration-300 cursor-pointer"
 >
 {t('productDetailPage.contactSupport')} →
 </button>
 </p>
 </div>
 </div>
 </div>
 </section>

 {/* 全产品对比组件 - 移到使用指南上面 */}
 {/* 根据产品类型判断使用哪个对比组件 */}
 {(selectedProductType?.includes('official') || type === 'renew' || ['240', '243', '246', '249'].includes(productId)) ? (
 <OfficialProductComparison currentProductName={productData?.name} />
 ) : (
 <ProductComparison currentProductName={productData?.name} />
 )}

 {/* What You Get & Universal Value - 核心功能和价值模块 */}
 <ProductFeaturesBenefits />

 {/* 使用教程部分 - 调整为与其他部分一致的高度 */}
 <section className="py-16 bg-cream-50">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="text-center mb-12">
 <div className="inline-flex items-center gap-3 mb-6">
 <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center">
 <Settings className="w-6 h-6 text-accent-600" />
 </div>
 <h2 className="text-3xl lg:text-4xl font-bold text-ink-900">{t('productDetailPage.tutorial.subtitle')}</h2>
 </div>
 </div>
 
 <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
 <div className="bg-cream-50 rounded-2xl p-6 shadow-lg border border-accent-100 hover:shadow-xl transition-all duration-300">
 <div className="flex items-start gap-4">
 <div className="flex-shrink-0 w-12 h-12 bg-accent-500 rounded-xl flex items-center justify-center mt-1">
 <ShoppingCart className="w-6 h-6 text-white" />
 </div>
 <div className="flex-1">
 <div className="flex items-center gap-2 mb-2">
 <span className="bg-accent-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold">1</span>
 <h3 className="text-lg font-bold text-ink-900">{t('productDetailPage.tutorial.step1.title')}</h3>
 </div>
 <p className="text-ink-700 leading-relaxed">{t('productDetailPage.tutorial.step1.description')}</p>
 </div>
 </div>
 </div>

 <div className="bg-cream-50 rounded-2xl p-6 shadow-lg border border-accent-100 hover:shadow-xl transition-all duration-300">
 <div className="flex items-start gap-4">
 <div className="flex-shrink-0 w-12 h-12 bg-accent-500 rounded-xl flex items-center justify-center mt-1">
 <Settings className="w-6 h-6 text-white" />
 </div>
 <div className="flex-1">
 <div className="flex items-center gap-2 mb-2">
 <span className="bg-accent-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold">2</span>
 <h3 className="text-lg font-bold text-ink-900">{t('productDetailPage.tutorial.step2.title')}</h3>
 </div>
 <p className="text-ink-700 leading-relaxed">{t('productDetailPage.tutorial.step2.description')}</p>
 </div>
 </div>
 </div>

 <div className="bg-cream-50 rounded-2xl p-6 shadow-lg border border-accent-100 hover:shadow-xl transition-all duration-300">
 <div className="flex items-start gap-4">
 <div className="flex-shrink-0 w-12 h-12 bg-accent-500 rounded-xl flex items-center justify-center mt-1">
 <LogIn className="w-6 h-6 text-white" />
 </div>
 <div className="flex-1">
 <div className="flex items-center gap-2 mb-2">
 <span className="bg-accent-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold">3</span>
 <h3 className="text-lg font-bold text-ink-900">{t('productDetailPage.tutorial.step3.title')}</h3>
 </div>
 <p className="text-ink-700 leading-relaxed">{t('productDetailPage.tutorial.step3.description')}</p>
 </div>
 </div>
 </div>
 </div>
 </div>
 </section>

 {/* Risk-Free Guarantees - 无风险保证 */}
 <RiskFreeGuarantees />

 {/* Universal FAQ - 常见问题解答 */}
 <UniversalFAQ />


 {/* 续费确认弹窗 */}
 {showRenewalModal && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
 <div className="bg-cream-50 rounded-xl shadow-md max-w-md w-full max-h-[90vh] overflow-y-auto">
 <div className="p-8">
 {/* 弹窗标题 */}
 <div className="text-center mb-6">
 <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
 <RefreshCw className="w-8 h-8 text-accent-500" />
 </div>
 <h2 className="text-2xl font-bold text-ink-900 mb-2">
 {t('renewalModal.title')}
 </h2>
 <p className="text-ink-600">
 {t('renewalModal.subtitle')}
 </p>
 </div>

 {/* 续费流程说明 */}
 <div className="space-y-4 mb-6">
 <div className="bg-accent-50 border border-accent-200 rounded-xl p-4">
 <div className="flex items-start gap-3">
 <div className="w-6 h-6 bg-accent-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
 <span className="text-white text-sm font-bold">1</span>
 </div>
 <div>
 <h4 className="font-semibold text-ink-900 mb-1">{t('renewalModal.steps.step1.title')}</h4>
 <p className="text-sm text-ink-600">
 {t('renewalModal.steps.step1.description')}
 </p>
 </div>
 </div>
 </div>

 <div className="bg-accent-50 border border-accent-200 rounded-xl p-4">
 <div className="flex items-start gap-3">
 <div className="w-6 h-6 bg-accent-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
 <span className="text-white text-sm font-bold">2</span>
 </div>
 <div>
 <h4 className="font-semibold text-ink-900 mb-1">{t('renewalModal.steps.step2.title')}</h4>
 <p className="text-sm text-ink-600">
 {t('renewalModal.steps.step2.description')}
 </p>
 </div>
 </div>
 </div>
 </div>

 {/* 客服联系方式 */}
 <div className="bg-cream-100 rounded-xl p-6 mb-6">
 <h4 className="font-semibold text-ink-900 mb-4 text-center">
 {t('renewalModal.contact.title')}
 </h4>
 <div className="space-y-3">
 <a
 href={`mailto:${t('renewalModal.contact.email.address')}?subject=${encodeURIComponent(i18n.language === 'zh-CN' ? 'ChatGPT续费咨询' : i18n.language === 'en-US' ? 'ChatGPT Renewal Consultation' : 'ChatGPT 갱신 상담')}&body=${encodeURIComponent(i18n.language === 'zh-CN' ? '您好，我需要咨询ChatGPT Plus官网账号续费服务。\n\n我的问题：\n\n联系方式：\n\n感谢您的帮助！' : i18n.language === 'en-US' ? 'Hello, I need to consult about ChatGPT Plus official account renewal service.\n\nMy question:\n\nContact information:\n\nThank you for your help!' : '안녕하세요, ChatGPT Plus 공식 계정 갱신 서비스에 대해 문의드리고 싶습니다.\n\n제 문의사항:\n\n연락처:\n\n도움 주셔서 감사합니다!')}`}
 className="flex items-center gap-3 p-3 bg-white rounded-lg border hover:bg-green-50 transition-colors cursor-pointer"
 onClick={(e) => {
 // 确保链接正常工作
 e.stopPropagation();
 }}
 >
 <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
 <span className="text-green-600 text-sm">📧</span>
 </div>
 <div>
 <p className="text-sm text-ink-500">{t('renewalModal.contact.email.label')}</p>
 <p className="font-medium text-ink-900">{t('renewalModal.contact.email.address')}</p>
 </div>
 </a>
 
 <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
 <div className="w-8 h-8 bg-accent-100 rounded-full flex items-center justify-center">
 <span className="text-accent-600 text-sm">📱</span>
 </div>
 <div>
 <p className="text-sm text-ink-500">{t('renewalModal.contact.telegram.label')}</p>
 <p className="font-medium text-ink-900">{t('renewalModal.contact.telegram.handle')}</p>
 </div>
 </div>

 {/* 右下角在线客服 */}
 <div className="flex items-center gap-3 p-3 bg-accent-50 rounded-lg border border-accent-200">
 <div className="w-8 h-8 bg-accent-100 rounded-full flex items-center justify-center">
 <span className="text-accent-600 text-sm">💬</span>
 </div>
 <div>
 <p className="text-sm text-ink-500">{t('renewalModal.contact.liveChat.label')}</p>
 <p className="font-medium text-ink-900">{t('renewalModal.contact.liveChat.description')}</p>
 </div>
 </div>
 
 </div>
 </div>

 {/* 按钮组 */}
 <div className="flex gap-3">
 <button
 onClick={() => setShowRenewalModal(false)}
 className="flex-1 px-6 py-3 border border-ink-300 text-ink-700 rounded-xl font-medium hover:bg-cream-100 transition-colors"
 >
 {t('renewalModal.buttons.cancel')}
 </button>
 <button
 onClick={() => {
 setShowRenewalModal(false);
 proceedToPurchase();
 }}
 className="flex-1 px-6 py-3 bg-accent-500 text-white rounded-xl font-medium hover:bg-accent-600 transition-all"
 >
 {t('renewalModal.buttons.confirm')}
 </button>
 </div>
 </div>
 </div>
 </div>
 )}


 </div>
 );
};

export default ProductDetailPage;